import React from 'react';
import {
	Modal,
	ModalOverlay,
	ModalContent,
	ModalBody,
	Box,
	Flex,
	HStack,
	Text,
	Icon,
	IconButton,
	Image,
	Input,
} from '@chakra-ui/react';
import { FiX, FiFileText, FiSend } from 'react-icons/fi';
import { formatFileSize } from 'utils/whatsappUtils';

/**
 * WhatsApp's "add a caption and send" stage: a dark canvas with the file in
 * the middle, a caption field, and one green send button.
 */
export default function AttachmentPreview({ attachment, isOpen, onClose, onSend, isSending }) {
	const [caption, setCaption] = React.useState('');
	React.useEffect(() => setCaption(''), [attachment]);

	if (!attachment) return null;
	const { file, kind, previewUrl } = attachment;

	const send = () => onSend({ caption: caption.trim() });

	return (
		<Modal isOpen={isOpen} onClose={onClose} size="full" motionPreset="scale">
			<ModalOverlay bg="wa.overlay" />
			<ModalContent bg="transparent" boxShadow="none" m="0">
				<Flex px="4" py="3" align="center" justify="space-between" color="whiteAlpha.900">
					<Text fontSize="sm" noOfLines={1}>
						{file.name} · {formatFileSize(file.size)}
					</Text>
					<IconButton aria-label="Cancel" icon={<Icon as={FiX} boxSize="5" />} variant="ghost" color="whiteAlpha.900" _hover={{ bg: 'whiteAlpha.200' }} onClick={onClose} />
				</Flex>
				<ModalBody display="flex" flexDirection="column" alignItems="center" justifyContent="center" p="4" h="calc(100vh - 64px)">
					<Box flex="1" display="flex" alignItems="center" justifyContent="center" minH="0" w="100%">
						{kind === 'image' && <Image src={previewUrl} alt={file.name} maxH="60vh" maxW="90vw" objectFit="contain" borderRadius="md" />}
						{kind === 'video' && <Box as="video" src={previewUrl} controls maxH="60vh" maxW="90vw" borderRadius="md" />}
						{kind === 'audio' && <Box as="audio" src={previewUrl} controls />}
						{kind === 'document' && (
							<HStack spacing="4" bg="whiteAlpha.100" px="6" py="5" borderRadius="lg" color="whiteAlpha.900">
								<Icon as={FiFileText} boxSize="10" />
								<Box>
									<Text fontSize="md" fontWeight="medium" noOfLines={1} maxW="60vw">
										{file.name}
									</Text>
									<Text fontSize="xs" color="whiteAlpha.700">
										{file.type || 'Document'} · {formatFileSize(file.size)}
									</Text>
								</Box>
							</HStack>
						)}
					</Box>
					<HStack w="100%" maxW="640px" mt="4" spacing="2">
						<Input
							value={caption}
							onChange={(e) => setCaption(e.target.value)}
							onKeyDown={(e) => e.key === 'Enter' && !isSending && send()}
							placeholder="Add a caption…"
							bg="wa.input"
							color="wa.ink"
							borderRadius="full"
							border="none"
							h="44px"
							_focus={{ boxShadow: '0 0 0 1px var(--chakra-colors-wa-accent)' }}
							autoFocus
						/>
						<IconButton
							aria-label="Send"
							icon={<Icon as={FiSend} boxSize="5" />}
							borderRadius="full"
							size="lg"
							bg="wa.accent"
							color="wa.accentInk"
							_hover={{ bg: 'wa.accentHover' }}
							isLoading={isSending}
							onClick={send}
						/>
					</HStack>
				</ModalBody>
			</ModalContent>
		</Modal>
	);
}
